import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessAuth } from '../contexts/BusinessAuthContext';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { toast } from 'sonner';

const BusinessDashboard = () => {
  const { businessCustomer, businessLogout } = useBusinessAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptions();
    fetchAnalytics();
  }, []);

  const fetchSubscriptions = async () => {
    const token = localStorage.getItem('business_token');
    
    try {
      const response = await axios.get(`${API_BASE}/business/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('business_token');
    
    try {
      const response = await axios.get(`${API_BASE}/business/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Don't show error toast, just log it
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleLogout = () => {
    businessLogout();
    toast.success('Logged out successfully');
    navigate('/business-login');
  };

  const getSubscriptionStatus = (subscription) => {
    const now = new Date();
    const expiresAt = new Date(subscription.expires_at);
    const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

    if (daysRemaining < 0) {
      return {status: 'expired', color: 'text-red-600 bg-red-50', text: 'Expired', days: 0};
    } else if (daysRemaining <= 7) {
      return {status: 'expiring', color: 'text-orange-600 bg-orange-50', text: 'Expiring Soon', days: daysRemaining};
    } else {
      return {status: 'active', color: 'text-green-600 bg-green-50', text: 'Active', days: daysRemaining};
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-emerald-600">
                🐾 Happy Paws Business Portal
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {businessCustomer?.business_name || businessCustomer?.email}
                </p>
                <p className="text-xs text-gray-500">Business Account</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-emerald-600 border border-gray-300 hover:border-emerald-300 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back!
          </h2>
          <p className="text-gray-600">
            Manage your business pins and monitor performance
          </p>
        </div>

        {/* Subscriptions Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Your Subscriptions
          </h3>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading subscriptions...</p>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">
                No active subscriptions found
              </p>
              <p className="text-sm text-gray-400">
                Contact your administrator to set up business pins
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((subscription) => {
                const statusInfo = getSubscriptionStatus(subscription);
                return (
                  <div
                    key={subscription.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {subscription.park_name || `Pin ${subscription.park_id}`}
                        </h4>
                        <p className="text-sm text-gray-500">
                          Park ID: {subscription.park_id}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                          {statusInfo.text}
                        </span>
                        {statusInfo.days > 0 && (
                          <p className="text-sm text-gray-600 mt-2">
                            {statusInfo.days} days remaining
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                      <p>Expires: {new Date(subscription.expires_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Analytics Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Performance Analytics (Last 30 Days)
          </h3>

          {analyticsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading analytics...</p>
            </div>
          ) : !analytics || analytics.total_views === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="text-6xl mb-4">📊</div>
              <p className="text-gray-600 font-medium mb-2">
                No Analytics Yet
              </p>
              <p className="text-sm text-gray-500">
                Analytics will appear once users start interacting with your offers
              </p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="text-sm font-medium text-blue-600 mb-1">Total Views</div>
                  <div className="text-3xl font-bold text-blue-900">{analytics.total_views}</div>
                  <div className="text-xs text-blue-600 mt-1">Offer modals opened</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                  <div className="text-sm font-medium text-emerald-600 mb-1">Total Clicks</div>
                  <div className="text-3xl font-bold text-emerald-900">{analytics.total_clicks}</div>
                  <div className="text-xs text-emerald-600 mt-1">Offer bubbles clicked</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="text-sm font-medium text-purple-600 mb-1">Email Requests</div>
                  <div className="text-3xl font-bold text-purple-900">{analytics.total_email_requests}</div>
                  <div className="text-xs text-purple-600 mt-1">Offers sent via email</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="text-sm font-medium text-orange-600 mb-1">CTR</div>
                  <div className="text-3xl font-bold text-orange-900">{analytics.ctr}%</div>
                  <div className="text-xs text-orange-600 mt-1">Click-through rate</div>
                </div>
              </div>

              {/* Per-Park Analytics */}
              {analytics.parks && analytics.parks.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Performance by Location</h4>
                  <div className="space-y-3">
                    {analytics.parks.map((park) => {
                      const parkCtr = park.views > 0 ? ((park.clicks / park.views) * 100).toFixed(1) : 0;
                      const parkConversion = park.clicks > 0 ? ((park.email_requests / park.clicks) * 100).toFixed(1) : 0;
                      
                      return (
                        <div
                          key={park.park_id}
                          className="border border-gray-200 rounded-lg p-4 hover:border-emerald-300 transition-colors"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div className="md:col-span-2">
                              <p className="font-medium text-gray-900">Park: {park.park_id.substring(0, 8)}...</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Views</p>
                              <p className="text-lg font-semibold text-blue-600">{park.views}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Clicks</p>
                              <p className="text-lg font-semibold text-emerald-600">{park.clicks}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Emails</p>
                              <p className="text-lg font-semibold text-purple-600">{park.email_requests}</p>
                            </div>
                          </div>
                          <div className="mt-2 flex gap-4 text-sm text-gray-600">
                            <span>CTR: <strong>{parkCtr}%</strong></span>
                            <span>Email Conversion: <strong>{parkConversion}%</strong></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Period Info */}
              <div className="mt-4 text-xs text-gray-500 text-center">
                Period: {analytics.period_start} to {analytics.period_end}
              </div>
            </>
          )}
        </div>

        {/* Payment Placeholder Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Subscription Management
          </h3>
          <div className="bg-emerald-50 rounded-lg p-8 text-center border-2 border-emerald-200 border-dashed">
            <div className="text-6xl mb-4">💳</div>
            <p className="text-emerald-800 font-medium mb-2">
              Payment Portal
            </p>
            <p className="text-sm text-emerald-700 mb-4">
              Manage your subscriptions and billing information
            </p>
            <p className="text-xs text-emerald-600">
              Contact your administrator for subscription renewals
            </p>
          </div>
        </div>

        {/* Offer Management Placeholder */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Offer Management
          </h3>
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">✨</div>
            <p className="text-gray-600 font-medium mb-2">
              Edit Your Special Offers
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Update offer details, terms, and expiration dates
            </p>
            <button
              disabled
              className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
            >
              Coming in Phase 5
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BusinessDashboard;
